import { ReactNode, useEffect, useRef, useState } from "react";
import { StyleSheetManager } from "styled-components";
import { createPortal } from "react-dom";

type JanelaRelatorioProps = {
  titulo: string;
  children: ReactNode;
  onClose: () => void;
};

export default function JanelaRelatorio({
  titulo,
  children,
  onClose,
}: JanelaRelatorioProps) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const janelaRef = useRef<Window | null>(null);

  const onCloseRef = useRef(onClose);

  /*
    Atualiza a referência sem recriar o efeito
    que abre a janela.
  */
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const janela = window.open(
      "",
      "relatorio-gerencial",
      [
        "width=1350",
        "height=820",
        "left=20",
        "top=20",
        "resizable=yes",
        "scrollbars=no",
      ].join(","),
    );

    if (!janela) {
      alert(
        "O navegador bloqueou a janela do relatório. " +
          "Autorize pop-ups para este site.",
      );

      onCloseRef.current();
      return;
    }

    janelaRef.current = janela;

    janela.document.open();

    janela.document.write(`
      <!DOCTYPE html>
      <html lang="pt-BR">
        <head>
          <meta charset="UTF-8" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0"
          />
          <title>${titulo}</title>

          <style>
            * {
              box-sizing: border-box;
            }

            html,
            body,
            #relatorio-gerencial-root {
              width: 100%;
              height: 100%;
              margin: 0;
            }

            body {
              padding: 0;
              background: #ffffff;
              font-family: Arial, Helvetica, sans-serif;
              overflow: hidden;
            }

            @media print {
              body {
                padding: 0;
                background: #ffffff;
              }

              button {
                display: none !important;
              }
            }
          </style>
        </head>

        <body>
          <div id="relatorio-gerencial-root"></div>
        </body>
      </html>
    `);

    janela.document.close();

    document
      .querySelectorAll<HTMLStyleElement>("style")
      .forEach((styleOriginal) => {
        const style = janela.document.createElement("style");

        style.textContent = styleOriginal.textContent;

        janela.document.head.appendChild(style);
      });

    const root = janela.document.getElementById(
      "relatorio-gerencial-root",
    ) as HTMLDivElement | null;

    if (!root) {
      janela.close();
      onCloseRef.current();
      return;
    }

    setContainer(root);

    janela.focus();

    const verificarFechamento = window.setInterval(() => {
      if (janela.closed) {
        window.clearInterval(verificarFechamento);

        onCloseRef.current();
      }
    }, 500);

    return () => {
      window.clearInterval(verificarFechamento);

      setContainer(null);

      /*
        Não feche automaticamente aqui.
        Em desenvolvimento, o React StrictMode pode
        executar o efeito duas vezes.
      */
    };
  }, [titulo]);

  if (!container) {
    return null;
  }

  if (!container || !janelaRef.current) {
    return null;
  }

  return createPortal(
    <StyleSheetManager target={janelaRef.current.document.head}>
      {children}
    </StyleSheetManager>,
    container,
  );
}
